import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Building2,
  Users,
  Plus,
  RefreshCw,
  Loader2,
  Eye,
  Trash2,
  UserPlus,
  Crown,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  TrendingUp,
  Shield,
  Star,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';

const TIER_COLORS = {
  standard: 'bg-slate-100 text-slate-700',
  professional: 'bg-blue-100 text-blue-700',
  enterprise: 'bg-purple-100 text-purple-700',
};

const TIER_ICONS = {
  standard: Shield,
  professional: Star,
  enterprise: Crown,
};

export default function AdminCorporateAccountsPage() {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    company_email: '',
    company_phone: '',
    billing_email: '',
    tier: 'standard',
  });
  const [memberForm, setMemberForm] = useState({
    email: '',
    role: 'member',
    can_purchase: false,
    can_view_reports: false,
  });
  const [saving, setSaving] = useState(false);
  const perPage = 10;

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.get('/admin/corporate-accounts', {
        params: { page, per_page: perPage }
      });
      setAccounts(response.data.accounts || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      toast.error('Failed to load corporate accounts');
    }
    setLoading(false);
  }, [page]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleCreate = async () => {
    if (!formData.company_name || !formData.company_email || !formData.billing_email) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      await adminAPI.post('/admin/corporate-accounts', formData);
      toast.success('Corporate account created');
      setCreateModalOpen(false);
      setFormData({
        company_name: '',
        company_email: '',
        company_phone: '',
        billing_email: '',
        tier: 'standard',
      });
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    }
    setSaving(false);
  };

  const handleViewAccount = async (account) => {
    try {
      const response = await adminAPI.get(`/admin/corporate-accounts/${account.id}`);
      setSelectedAccount(response.data);
      setViewModalOpen(true);
    } catch (error) {
      toast.error('Failed to load account details');
    }
  };

  const handleAddMember = async () => {
    if (!memberForm.email) {
      toast.error('Please enter an email');
      return;
    }
    
    setSaving(true);
    try {
      await adminAPI.post(`/admin/corporate-accounts/${selectedAccount.id}/members`, memberForm);
      toast.success('Member added');
      setAddMemberModalOpen(false);
      setMemberForm({ email: '', role: 'member', can_purchase: false, can_view_reports: false });
      handleViewAccount(selectedAccount);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
    setSaving(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-6" data-testid="admin-corporate-accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Corporate Accounts</h2>
          <p className="text-slate-500">Manage team accounts with seat management and volume discounts</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchAccounts} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setCreateModalOpen(true)} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Create Account
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Accounts</p>
                <p className="text-3xl font-bold text-navy-700">{total}</p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Seats Used</p>
                <p className="text-3xl font-bold text-green-600">
                  {accounts.reduce((sum, a) => sum + (a.member_count || 0), 0)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Enterprise</p>
                <p className="text-3xl font-bold text-purple-600">
                  {accounts.filter(a => a.tier === 'enterprise').length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-100">
                <Crown className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Avg Discount</p>
                <p className="text-3xl font-bold text-orange-600">
                  {accounts.length > 0 
                    ? Math.round(accounts.reduce((sum, a) => sum + (a.discount_percentage || 0), 0) / accounts.length)
                    : 0}%
                </p>
              </div>
              <div className="p-3 rounded-full bg-orange-100">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accounts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-navy-500" />
            Corporate Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No corporate accounts yet</p>
              <Button onClick={() => setCreateModalOpen(true)} className="mt-4" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Create First Account
              </Button>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Seats</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((account) => {
                    const TierIcon = TIER_ICONS[account.tier] || Shield;
                    return (
                      <TableRow key={account.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{account.company_name}</p>
                            <p className="text-sm text-slate-500">{account.company_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${TIER_COLORS[account.tier]} gap-1`}>
                            <TierIcon className="w-3 h-3" />
                            {account.tier}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{account.member_count || 0}</span>
                          <span className="text-slate-400"> / {account.max_seats}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-green-600 font-medium">{account.discount_percentage}%</span>
                        </TableCell>
                        <TableCell>{formatDate(account.created_at)}</TableCell>
                        <TableCell>
                          <Badge className={account.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {account.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleViewAccount(account)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Account Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Corporate Account</DialogTitle>
            <DialogDescription>Set up a new corporate account with team seats</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Acme Corporation"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Email *</Label>
                <Input
                  type="email"
                  value={formData.company_email}
                  onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                  placeholder="contact@company.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.company_phone}
                  onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Billing Email *</Label>
              <Input
                type="email"
                value={formData.billing_email}
                onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
                placeholder="billing@company.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Plan Tier</Label>
              <Select value={formData.tier} onValueChange={(v) => setFormData({ ...formData, tier: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard (10 seats, 10% off)</SelectItem>
                  <SelectItem value="professional">Professional (50 seats, 20% off)</SelectItem>
                  <SelectItem value="enterprise">Enterprise (Unlimited, 30% off)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Account Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedAccount?.company_name}</DialogTitle>
            <DialogDescription>{selectedAccount?.company_email}</DialogDescription>
          </DialogHeader>
          
          {selectedAccount && (
            <div className="space-y-6 py-4">
              {/* Account Info */}
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-navy-700">{selectedAccount.members?.length || 0}</p>
                  <p className="text-sm text-slate-500">Members</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{selectedAccount.discount_percentage}%</p>
                  <p className="text-sm text-slate-500">Discount</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{selectedAccount.max_seats}</p>
                  <p className="text-sm text-slate-500">Max Seats</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <Badge className={TIER_COLORS[selectedAccount.tier]}>
                    {selectedAccount.tier}
                  </Badge>
                </div>
              </div>

              {/* Members */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold">Team Members</h4>
                  <Button size="sm" onClick={() => setAddMemberModalOpen(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Member
                  </Button>
                </div>
                
                {selectedAccount.members?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedAccount.members.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>{member.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{member.role}</Badge>
                          </TableCell>
                          <TableCell>{formatDate(member.joined_at || member.invited_at)}</TableCell>
                          <TableCell>
                            <Badge className={member.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                              {member.is_active ? 'Active' : 'Pending'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">No members yet</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Member Modal */}
      <Dialog open={addMemberModalOpen} onOpenChange={setAddMemberModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>Invite a new member to {selectedAccount?.company_name}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={memberForm.email}
                onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                placeholder="member@company.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Role</Label>
              <Select value={memberForm.role} onValueChange={(v) => setMemberForm({ ...memberForm, role: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={memberForm.can_purchase}
                  onChange={(e) => setMemberForm({ ...memberForm, can_purchase: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Can Purchase</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={memberForm.can_view_reports}
                  onChange={(e) => setMemberForm({ ...memberForm, can_view_reports: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">View Reports</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMemberModalOpen(false)}>Cancel</Button>
            <Button onClick={handleAddMember} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
